import { useUser } from 'Core/Hooks/useUser'
import { enqueueNotification } from 'Utils/Helpers'
import { FullScreenModal, LabelContent, LoadingFeedback, SwitchBoxLabelContent } from 'V2Components'
import { RDPStorageConfigModal } from 'features/sessions'
import { updateTenantProfile } from 'features/settings'
import { createDataSelectorHook, useReduxFetch } from 'infra/redux'
import { updateSliceData } from 'infra/redux/sliceHandlers'
import { Button, IconButton, Label, Typography } from 'procyon-ui'
import React, { useState } from 'react'
import { ApiKeys } from './components/ApiKeys'
import { faCopy } from '@fortawesome/free-solid-svg-icons'
import { ConfirmationModal } from './components/ApiKeys/ConfirmModal'

const useSlices = createDataSelectorHook(['tenantprofiles'])

function TenantInformation({}) {
  const [editMode, setEditMode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showRDPConfigModal, setShowRDPConfigModal] = useState(false)

  const { user } = useUser()
  const { slices } = useSlices()

  const tenantProfile = slices.tenantprofiles.find(
    (e) => e.ObjectMeta.Name === user.ObjectMeta.Tenant
  )

  const config = getConfig(tenantProfile)

  const [tenantForm, setTenantForm] = useState({
    InlineMode: false,
    NoDnsProxyMode: false,
    PrivateProxyOnlyMode: false,
    TunnelLocalAddr: '',
    TunnelRemoteAddr: '',
    TunnelSubnet: '',
    TunnelListenerCidr: '',
    Mandatory: false,
    AuthCheckInterval: '',
    AllowedFailedAttempts: '',
    FailedAttemptsWindow: '',
    AuthType: '0',
    EnableMFA: false,
    EnableSessionLogging: false,
    EnableSessionOutputLogging: false,
    EnableRDPRecording: false,
    loaded: false,
    AgentLess: false,
    IdpType: '',
    ClientID: '',
    ClientSecret: '',
    OidcDomain: '',
    SSOURL: '',
    IssuerURL: '',
    Metadata: '',
    EntityID: '',
    LoginMode: '' // mix || agentless
  })

  const handleTenantFormChange = (v = {}) => {
    setTenantForm((s) => ({ ...s, ...v }))
  }

  if (config && !tenantForm.loaded) {
    setTenantForm({ ...config, loaded: true })
  }

  const handleCancel = () => {
    setEditMode(false)
    setTenantForm({ ...config, loaded: true })
  }

  const handleTenantCopy = (name) => {
    navigator.clipboard.writeText(name)
    enqueueNotification(`Tenant Name Copied to clipboard!`, 'info')
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    const tp = await handleTenantProfileUpdate(tenantForm, tenantProfile)
    enqueueNotification('Tenant Information updated successfully!', 'info')
    setEditMode(false)
    setIsLoading(false)
    updateSliceData(tp)
  }

  useReduxFetch(['tenantprofiles'])

  // Proxy Confirmation State
  const [pendingProxyValue, setPendingProxyValue] = useState(null)
  const [showProxyConfirmModal, setShowProxyConfirmModal] = useState(false)

  const onProxyToggleChange = () => {
    const newValue = !tenantForm.PrivateProxyOnlyMode
    setPendingProxyValue(newValue)
    setShowProxyConfirmModal(true)
  }

  const confirmProxyToggle = () => {
    handleTenantFormChange({ PrivateProxyOnlyMode: pendingProxyValue })
    setPendingProxyValue(null)
    setShowProxyConfirmModal(false)
  }

  const cancelProxyToggle = () => {
    setPendingProxyValue(null)
    setShowProxyConfirmModal(false)
  }

  return (
    <div>
      <div className='flex justify-between items-center mb-8'>
        <Typography style={{ color: 'var(--color-font-base)' }} variant='h4-regular'>
          Tenant Information
        </Typography>

        {!editMode && (
          <Button disabled={!tenantProfile} onClick={() => setEditMode(true)} variant='gray'>
            Edit
          </Button>
        )}

        {editMode && (
          <div className='flex gap-4'>
            <Button onClick={handleCancel} variant='grayRed'>
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant='primary'>
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <LabelContent
        content={
          <div className='flex items-center'>
            {user.ObjectMeta.Tenant}
            <IconButton
              onClick={() => handleTenantCopy(user.ObjectMeta.Tenant)}
              icon={faCopy}
              variant='clear'
            />
          </div>
        }
        title='Tenant'
      />

      <SwitchBoxLabelContent
        editMode={editMode}
        label='Session Logging'
        onChange={(v) => handleTenantFormChange({ EnableSessionLogging: v })}
        value={tenantForm.EnableSessionLogging}
      />

      {tenantForm.EnableSessionLogging && (
        <SwitchBoxLabelContent
          editMode={editMode}
          label='Server Output Logging'
          onChange={(v) => handleTenantFormChange({ EnableSessionOutputLogging: v })}
          value={tenantForm.EnableSessionOutputLogging}
        />
      )}

      <SwitchBoxLabelContent
        editMode={editMode}
        label='RDP Recording'
        onChange={(v) => handleTenantFormChange({ EnableRDPRecording: v })}
        value={tenantForm.EnableRDPRecording}
      />

      {/* Proxy Mode with Confirmation */}
      <SwitchBoxLabelContent
        editMode={editMode}
        label='Private Proxy Only Mode'
        onChange={onProxyToggleChange}
        value={
          showProxyConfirmModal && pendingProxyValue !== null
            ? pendingProxyValue
            : tenantForm.PrivateProxyOnlyMode
        }
      />

      <LabelContent
        content={
          <div className='flex items-center'>
            <Label
              variant={tenantProfile?.Spec?.AgentLess ? 'success' : 'gray'}
              text={tenantProfile?.Spec?.AgentLess ? 'Enabled' : 'Disabled'}
            />
          </div>
        }
        title='AgentLess'
      />

      <Typography style={{ color: 'var(--color-font-base)' }} className='my-8' variant='h4-regular'>
        For Developers
      </Typography>

      <ApiKeys />

      {showRDPConfigModal && (
        <RDPStorageConfigModal onCancel={() => setShowRDPConfigModal(false)} />
      )}

      {/* Proxy Confirmation Modal */}
      {showProxyConfirmModal && (
        <div className='fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50'>
          <ConfirmationModal
            title={
              pendingProxyValue
                ? 'Enable Private Proxy Only Mode?'
                : 'Disable Private Proxy Only Mode?'
            }
            message={
              pendingProxyValue
                ? 'Are you sure you want to enable Private Proxy Only Mode?'
                : 'Are you sure you want to disable Private Proxy Only Mode?'
            }
            onCancel={cancelProxyToggle}
            onConfirm={confirmProxyToggle}
          />
        </div>
      )}

      <LoadingFeedback message='Updating Tenant Info' caption='Please wait..' loading={isLoading} />
    </div>
  )
}

const getConfig = (tenantProfiles) => {
  if (!tenantProfiles?.Spec) return null
  const {
    TunnelConfig,
    MFAttributes,
    EnableMFA,
    EnableSessionLogging,
    EnableSessionOutputLogging,
    EnableRDPRecording,
    AgentLess,
    IdpType,
    OidcAttributes,
    SamlAttributes,
    LoginMode
  } = tenantProfiles.Spec
  const {
    InlineMode,
    NoDnsProxyMode,
    PrivateProxyOnlyMode,
    TunnelLocalAddr,
    TunnelRemoteAddr,
    TunnelSubnet,
    TunnelListenerCidr
  } = TunnelConfig
  const { Mandatory, AuthCheckInterval, AllowedFailedAttempts, FailedAttemptsWindow, AuthType } =
    MFAttributes
  const { ClientID, ClientSecret, OidcDomain } = OidcAttributes
  const { SSOURL, IssuerURL, Metadata, EntityID } = SamlAttributes

  return {
    InlineMode,
    NoDnsProxyMode,
    PrivateProxyOnlyMode,
    TunnelLocalAddr,
    TunnelRemoteAddr,
    TunnelSubnet,
    TunnelListenerCidr,
    Mandatory,
    AuthCheckInterval,
    AllowedFailedAttempts,
    FailedAttemptsWindow,
    AuthType,
    EnableMFA,
    EnableSessionLogging,
    EnableSessionOutputLogging,
    EnableRDPRecording,
    AgentLess,
    IdpType,
    ClientID,
    ClientSecret,
    OidcDomain,
    SSOURL,
    IssuerURL,
    Metadata,
    EntityID,
    LoginMode
  }
}

const handleTenantProfileUpdate = async (data, obj) => {
  const {
    InlineMode,
    NoDnsProxyMode,
    PrivateProxyOnlyMode,
    TunnelLocalAddr,
    TunnelRemoteAddr,
    TunnelSubnet,
    TunnelListenerCidr,
    Mandatory,
    AuthCheckInterval,
    AllowedFailedAttempts,
    FailedAttemptsWindow,
    AuthType,
    EnableMFA,
    EnableSessionLogging,
    EnableSessionOutputLogging,
    EnableRDPRecording,
    AgentLess,
    IdpType,
    ClientID,
    ClientSecret,
    OidcDomain,
    SSOURL,
    IssuerURL,
    Metadata,
    EntityID,
    LoginMode
  } = data

  const MFAttributes = {
    Mandatory,
    AuthCheckInterval,
    AllowedFailedAttempts,
    FailedAttemptsWindow,
    AuthType
  }

  const OidcAttributes = {
    ClientID,
    ClientSecret,
    OidcDomain
  }

  const SamlAttributes = {
    SSOURL,
    IssuerURL,
    Metadata,
    EntityID
  }

  const TunnelConfig = {
    InlineMode,
    NoDnsProxyMode,
    PrivateProxyOnlyMode,
    TunnelLocalAddr,
    TunnelRemoteAddr,
    TunnelSubnet,
    TunnelListenerCidr
  }

  const Spec = {
    ...obj.Spec,
    TunnelConfig,
    MFAttributes,
    EnableMFA,
    EnableSessionLogging,
    EnableSessionOutputLogging,
    EnableRDPRecording,
    AgentLess,
    IdpType,
    OidcAttributes,
    SamlAttributes,
    LoginMode
  }
  const payload = structuredClone(obj)
  payload.Spec = Spec

  return await updateTenantProfile(payload)
}

export { TenantInformation }
